/**
 * Leads (L1 + L2). Contrato: argenlotapi/docs/frontend/FRONTEND_LEADS.md.
 * Map: docs/maps/leads.md.
 */

import { getDayGroupLabel, groupByDay } from '../Utils/dayGroups';

/** Datos del cliente (`profile`): rótulos de campo para errores y timeline (`profile.<campo>`). */
export const LEAD_PROFILE_FIELD_LABELS = {
  first_name: 'nombre de pila',
  last_name: 'apellido',
  birth_date: 'fecha de nacimiento',
  gender: 'género',
  marital_status: 'estado civil',
  children_count: 'hijos',
  children_birth_years: 'hijos',
  employment_status: 'situación laboral',
  occupation: 'ocupación',
  residence_location: 'zona',
  preferred_contact_channels: 'contacto preferido',
  preferred_contact_times: 'horario de contacto',
  housing_status: 'vivienda',
};

/** Orden del embudo. Claves en inglés (API), labels en español. */
export const LEAD_STAGES = ['new', 'contacted', 'in_progress', 'won', 'lost'];

export const LEAD_STAGE_LABELS = {
  new: 'Nuevo',
  contacted: 'Contactado',
  in_progress: 'En seguimiento',
  won: 'Ganado',
  lost: 'Perdido',
};

/** Color de acento por etapa (chip / borde de tarjeta). */
export const LEAD_STAGE_COLORS = {
  new: '#6DA8A7',
  contacted: '#7AA2E3',
  in_progress: '#E3B04B',
  won: '#5FB878',
  lost: '#E07A7A',
};

/** Temperatura del lead (API: hot/warm/cold). Tablero principal V3. */
export const LEAD_TEMPERATURES = ['hot', 'warm', 'cold'];

export const LEAD_TEMPERATURE_LABELS = {
  hot: 'Caliente',
  warm: 'Tibio',
  cold: 'Frío',
};

export const LEAD_TEMPERATURE_COLORS = {
  hot: '#E07A5F',
  warm: '#E3B04B',
  cold: '#7A8FA8',
};

export function getLeadTemperatureLabel(temperature) {
  return LEAD_TEMPERATURE_LABELS[temperature] || '';
}

/**
 * Etapas por interés (claves ES de la API: interest_summary.top_stage / item.stage).
 * Distinto de LEAD_STAGES (legado en inglés, display derivado).
 */
export const INTEREST_STAGES = [
  'nuevo', 'contactado', 'en_seguimiento', 'ofertado', 'reservado', 'cerrado', 'descartado',
];

export const INTEREST_STAGE_LABELS = {
  nuevo: 'Nuevo',
  contactado: 'Contactado',
  en_seguimiento: 'En seguimiento',
  ofertado: 'Ofertado',
  reservado: 'Reservado',
  cerrado: 'Cerrado',
  descartado: 'Descartado',
};

export function getInterestStageLabel(stage) {
  if (!stage) return '—';
  return INTEREST_STAGE_LABELS[stage] || LEAD_STAGE_LABELS[stage] || String(stage);
}

/** Color del punto de etapa por interés (V3-3). */
export const INTEREST_STAGE_COLORS = {
  nuevo: '#6DA8A7',
  contactado: '#7AA2E3',
  en_seguimiento: '#E3B04B',
  ofertado: '#C89BE3',
  reservado: '#E39B5F',
  cerrado: '#5FB878',
  descartado: '#E07A7A',
};

/** Ítems de registro: los únicos con etapa (lista / showroom no). */
export const INTEREST_TRACKABLE_KINDS = ['user_lot', 'cadastral_lot', 'cad_parcel'];

export function isInterestTrackable(item) {
  return INTEREST_TRACKABLE_KINDS.includes(item?.item_kind);
}

/** Etapas que pueden tocar el stock (`apply_stock`). */
export const INTEREST_STOCK_STAGES = ['reservado', 'cerrado'];

/** Lote catastral: sin stock propio (la API responde `NO_STOCK`). */
export function interestHasStock(item) {
  return item?.item_kind === 'user_lot' || item?.item_kind === 'cad_parcel';
}

/**
 * ¿Hace falta preguntar por el stock al pasar de `from` a `to`? Solo si el
 * registro tiene stock y la etapa entra o sale de reservado / cerrado.
 */
export function interestStageTouchesStock(item, from, to) {
  if (!interestHasStock(item) || from === to) return false;
  return INTEREST_STOCK_STAGES.includes(to) || INTEREST_STOCK_STAGES.includes(from);
}

/**
 * Monto de oferta: "USD 120.000" / "ARS 35.000.000,50". Sin monto → ''.
 *
 * @param {number|string|null} amount
 * @param {string|null} currency
 */
export function formatInterestOffer(amount, currency) {
  if (amount === null || amount === undefined || amount === '') return '';
  const n = Number(amount);
  if (!Number.isFinite(n)) return '';
  const text = n.toLocaleString('es-AR', { maximumFractionDigits: 2 });
  return currency ? `${String(currency).toUpperCase()} ${text}` : text;
}

const STOCK_STATUS_LABELS = {
  available: 'disponible',
  reserved: 'reservada',
  sold: 'vendida',
};

/**
 * Toast de `stock_update` (V3-3). `null` → '' (la transición no tocaba stock).
 *
 * @param {object|null} stockUpdate `{ applied, before, after, reason }`
 * @returns {{ kind: 'success'|'info', text: string }|null}
 */
export function describeInterestStockUpdate(stockUpdate) {
  if (!stockUpdate || typeof stockUpdate !== 'object') return null;
  const after = STOCK_STATUS_LABELS[stockUpdate.after] || stockUpdate.after || '';
  if (stockUpdate.applied) {
    return { kind: 'success', text: after ? `Stock actualizado: quedó ${after}.` : 'Stock actualizado.' };
  }
  const reasons = {
    OPT_OUT: '',
    NO_STOCK: '',
    FORBIDDEN: 'No se actualizó el stock: no podés editar ese registro.',
    RECORD_NOT_FOUND: 'No se actualizó el stock: el registro ya no existe.',
    ALREADY_SET: after ? `El stock ya estaba ${after}.` : '',
    STRONGER_STATUS: 'No se cambió el stock: ya figura como vendido.',
    CHANGED_ELSEWHERE: 'No se cambió el stock: alguien lo modificó desde otro lado.',
    NOT_SET_BY_INTEREST: 'No se cambió el stock: no lo había marcado este interés.',
  };
  const text = Object.prototype.hasOwnProperty.call(reasons, stockUpdate.reason)
    ? reasons[stockUpdate.reason]
    : '';
  return text ? { kind: 'info', text } : null;
}

/**
 * Interés de una consulta: mismo registro (`parcel_id` / `user_lot_id` /
 * `lot_id`). Solo ítems con etapa; sin match claro → null.
 *
 * @param {object} inquiry
 * @param {object[]} items
 */
export function findInquiryInterest(inquiry, items) {
  if (!inquiry || !Array.isArray(items)) return null;
  const refs = [
    ['cad_parcel', 'parcel_id'],
    ['user_lot', 'user_lot_id'],
    ['cadastral_lot', 'lot_id'],
  ];
  const ref = refs.find(([, key]) => inquiry[key]);
  if (!ref) return null;
  const [kind, key] = ref;
  const matches = items.filter((it) => it?.item_kind === kind && it[key] && String(it[key]) === String(inquiry[key]));
  return matches.length === 1 && matches[0].stage ? matches[0] : null;
}

/**
 * Resumen compacto para la tarjeta: "3 intereses" / "1 interés".
 * Solo el conteo (interest_count o item_count). La etapa vive en cada interés
 * (V3-3), no en la meta de la lista.
 *
 * @param {object|null} lead
 * @returns {string}
 */
export function describeLeadInterestSummary(lead) {
  const countRaw = Number(lead?.interest_count ?? lead?.item_count);
  const count = Number.isFinite(countRaw) && countRaw > 0 ? Math.trunc(countRaw) : 0;
  if (count) return `${count} ${count === 1 ? 'interés' : 'intereses'}`;
  return '';
}

export const LEAD_LOST_REASONS = [
  'not_interested',
  'bought_elsewhere',
  'budget',
  'no_response',
  'stock_unavailable',
  'other',
];

export const LEAD_LOST_REASON_LABELS = {
  not_interested: 'No le interesa',
  bought_elsewhere: 'Compró en otro lado',
  budget: 'Presupuesto',
  no_response: 'No responde',
  stock_unavailable: 'Lote no disponible',
  other: 'Otro',
};

export const LEAD_SOURCE_LABELS = {
  manual: 'Carga manual',
  inquiry: 'Consulta',
  other: 'Otro canal',
};

/**
 * Canal del lead (`lead.channel`, puede faltar o ser `null`). `share_form` lo
 * pone la API en los leads que crea una consulta: se muestra, no se elige.
 */
export const LEAD_CHANNEL_LABELS = {
  share_form: 'Formulario',
  whatsapp: 'WhatsApp',
  phone: 'Teléfono',
  in_person: 'Presencial',
  social: 'Redes',
  portal: 'Portal',
  other: 'Otro',
};

/** Canales elegibles a mano (alta / edición). */
export const LEAD_CHANNEL_OPTIONS = ['whatsapp', 'phone', 'in_person', 'social', 'portal', 'other'];

export function getLeadChannelLabel(channel) {
  return LEAD_CHANNEL_LABELS[channel] || '';
}

/**
 * Origen visible del lead, igual en tarjeta, subtítulo y fila de Datos:
 * consulta (`source=inquiry` o `channel=share_form`, incluye lote compartido)
 * → “Desde link compartido”; canal → “Desde WhatsApp”…; `source=other` sin canal →
 * “Desde Otro canal”; manual sin canal → “Manual”.
 *
 * @param {{ source?: string, channel?: string|null }|null} lead
 * @returns {{ label: string, kind: 'inquiry'|'channel'|'manual' }}
 */
export function getLeadOriginLabel(lead) {
  const channel = lead?.channel || '';
  if (channel === 'share_form' || lead?.source === 'inquiry') {
    return { label: 'Desde link compartido', kind: 'inquiry' };
  }
  if (isLeadChannelSelectable(channel)) {
    return { label: `Desde ${channel === 'other' ? 'Otro canal' : getLeadChannelLabel(channel)}`, kind: 'channel' };
  }
  if (lead?.source === 'other') return { label: 'Desde Otro canal', kind: 'channel' };
  return { label: 'Manual', kind: 'manual' };
}

export function isLeadChannelSelectable(channel) {
  return LEAD_CHANNEL_OPTIONS.includes(channel);
}

/**
 * Consulta cargada a mano (`POST /createmanualinquiry`): `origin:
 * manual_entry` / `collection_kind: manual`. No viene de ninguna lista.
 *
 * @param {object|null|undefined} inquiry
 * @returns {boolean}
 */
export function isManualInquiry(inquiry) {
  return inquiry?.origin === 'manual_entry' || inquiry?.collection_kind === 'manual';
}

/**
 * Fecha a mostrar de una consulta: `occurred_at ?? created_at` (la manual
 * puede tener la fecha real del llamado; las del share traen `null`).
 *
 * @param {object|null|undefined} inquiry
 * @returns {string}
 */
export function getInquiryDisplayAt(inquiry) {
  return inquiry?.occurred_at || inquiry?.created_at || '';
}

/**
 * Chips de origen de una consulta manual: «Manual» + canal («WhatsApp»…).
 * Sirve para la consulta (`origin`, `channel`) y para el payload de
 * `inquiry_manual_added`. No manual → [].
 *
 * @param {object|null|undefined} source Consulta o payload de la actividad.
 * @param {{ manual?: boolean }} [options] `manual: true` fuerza (actividad).
 * @returns {string[]}
 */
export function getManualInquiryTags(source, { manual = false } = {}) {
  if (!manual && !isManualInquiry(source)) return [];
  const channel = getLeadChannelLabel(source?.channel);
  return ['Manual', channel].filter(Boolean);
}

/**
 * Toast al cargar una consulta manual: “Consulta cargada · lead nuevo · 3
 * registros”. `result` = `readManualInquiryResult`.
 *
 * @param {{ leadCreated?: boolean, unarchived?: boolean, inquiries?: object[], duplicatesIgnored?: number }} result
 * @returns {string}
 */
export function describeManualInquiryCreated(result) {
  const n = Array.isArray(result?.inquiries) ? result.inquiries.length : 0;
  return [
    n > 1 ? `${n} consultas cargadas` : 'Consulta cargada',
    result?.leadCreated ? 'lead nuevo' : '',
    result?.unarchived ? 'lead desarchivado' : '',
    result?.duplicatesIgnored ? `${result.duplicatesIgnored} registro${result.duplicatesIgnored === 1 ? '' : 's'} repetido${result.duplicatesIgnored === 1 ? '' : 's'}` : '',
  ].filter(Boolean).join(' · ');
}

/** ¿La actividad es una consulta (del share / vinculada o cargada a mano)? */
export function isLeadInquiryActivity(action) {
  return action === 'inquiry_linked' || action === 'inquiry_manual_added';
}

/** Filtro "Origen" del tablero (L2 histórico): `getleads?source=`. '' = todos.
 * Reemplazado en UI por chips de Seguimiento (`LEAD_FOLLOWUP_FILTER_OPTIONS`). */
export const LEAD_SOURCE_FILTER_OPTIONS = [
  { id: '', label: 'Todos' },
  { id: 'inquiry', label: 'Consultas' },
  { id: 'manual', label: 'Manual' },
  { id: 'other', label: 'Otro canal' },
];

/** Días sin contacto para el chip "Sin seguimiento" (`inactive_days`). */
export const LEAD_FOLLOWUP_INACTIVE_DAYS = 7;

/**
 * Chips de seguimiento del panel (API DEV 42c97fb, FRONTEND_LEADS § Chips de
 * seguimiento). Selección única; default `all`.
 */
export const LEAD_FOLLOWUP_FILTER_OPTIONS = [
  { id: 'all', label: 'Todos' },
  { id: 'unseen', label: 'Sin leer' },
  { id: 'inactive', label: 'Sin seguimiento' },
  { id: 'offer_reserve', label: 'Con oferta o reserva' },
];

/**
 * Params de `getLeads` para un chip de seguimiento.
 * @param {string} followUpId
 * @returns {{ unseen?: boolean, inactiveDays?: number, interestStages?: string[] }}
 */
export function buildLeadFollowUpQuery(followUpId) {
  switch (followUpId) {
    case 'unseen':
      return { unseen: true };
    case 'inactive':
      return { inactiveDays: LEAD_FOLLOWUP_INACTIVE_DAYS };
    case 'offer_reserve':
      return { interestStages: ['ofertado', 'reservado'] };
    default:
      return {};
  }
}

/**
 * Copy visible: lista / lote (`.cursor/rules/ui-copy-lista-lote.mdc`). Los
 * `item_kind` internos `showroom` / `cad_parcel` no se muestran.
 */
export const LEAD_ITEM_KIND_LABELS = {
  cadastral_lot: 'Lote',
  user_lot: 'Unidad',
  cad_parcel: 'Lote',
  list: 'Lista',
  showroom: 'Lista',
};

function idOrEmpty(value) {
  return value == null ? '' : String(value).trim();
}

/**
 * `item_label` que es solo la nomenclatura catastral (dígitos y separadores,
 * ≥ 8 dígitos). La API arma el label de un lote catastral con `lots.name`,
 * que en Córdoba es la nomenclatura: no hay dirección ni nombre legible.
 *
 * @param {string} value
 * @returns {boolean}
 */
export function isCadastralNumberLabel(value) {
  const text = String(value || '').trim();
  if (!/^[\d.\-/\s]+$/.test(text)) return false;
  return text.replace(/\D/g, '').length >= 8;
}

/**
 * Cómo mostrar el label de un interés / consulta. Con un nombre legible
 * (`LOTE 15`, `Depto 2C`) va tal cual. Si es solo la nomenclatura, primero el
 * tipo (“Lote”) y el número como secundario: “Lote 1101010412018022”.
 *
 * @param {string} label `item_label` (snapshot).
 * @param {string} [kind] `item_kind`.
 * @returns {{ primary: string, secondary: string, text: string, isCadastral: boolean }}
 */
export function getLeadItemLabelParts(label, kind) {
  const text = String(label || '').trim();
  if (isCadastralNumberLabel(text)) {
    const prefix = LEAD_ITEM_KIND_LABELS[kind] || 'Lote';
    return {
      primary: prefix, secondary: text, text: `${prefix} ${text}`, isCadastral: true,
    };
  }
  return {
    primary: text, secondary: '', text, isCadastral: false,
  };
}

/** Los 5 ids que definen un ítem (con `item_kind`): mismo criterio que la API para repetidos. */
const LEAD_ITEM_ID_KEYS = ['lot_id', 'user_lot_id', 'parcel_id', 'list_id', 'project_id'];

const LEAD_ITEM_REQUIRED_IDS = {
  cadastral_lot: ['lot_id'],
  user_lot: ['user_lot_id'],
  cad_parcel: ['parcel_id', 'project_id'],
  list: ['list_id'],
  showroom: ['project_id'],
};

/**
 * Ítem para `createlead.items` / `updatelead.add_items`. Solo manda los ids
 * presentes; `list_id` en lote / unidad es contexto (cuenta para repetidos:
 * el mismo lote con y sin lista son dos ítems). Sin los ids obligatorios del
 * tipo → `null`.
 *
 * @param {{ kind: string, lotId?: string, userLotId?: string, parcelId?: string,
 *   listId?: string, projectId?: string, label?: string }} args
 * @returns {object|null}
 */
export function buildLeadItem({
  kind, lotId, userLotId, parcelId, listId, projectId, label,
} = {}) {
  const required = LEAD_ITEM_REQUIRED_IDS[kind];
  if (!required) return null;
  const ids = {
    lot_id: idOrEmpty(lotId),
    user_lot_id: idOrEmpty(userLotId),
    parcel_id: idOrEmpty(parcelId),
    list_id: idOrEmpty(listId),
    project_id: idOrEmpty(projectId),
  };
  if (required.some((key) => !ids[key])) return null;
  const item = { item_kind: kind };
  LEAD_ITEM_ID_KEYS.forEach((key) => {
    if (ids[key]) item[key] = ids[key];
  });
  const itemLabel = String(label || '').trim().slice(0, 200);
  if (itemLabel) item.item_label = itemLabel;
  return item;
}

/**
 * Adónde lleva tocar un interés del lead. Sin los ids que necesita → `null`
 * (el chip no es clicable).
 *
 * - `cadastral_lot` → `{ type: 'lot', lotId }` (ficha del lote)
 * - `user_lot` → `{ type: 'unit', userLotId }` (se resuelve el lote de la UF)
 * - `list` → `{ type: 'list', listId, listName }`
 * - `showroom` → `{ type: 'cad', projectId }` (lista CAD)
 * - `cad_parcel` → `{ type: 'cad', projectId, parcelId, parcelLabel }`
 *
 * @param {object} item Fila de `items` del lead.
 * @returns {object|null}
 */
export function getLeadItemTarget(item) {
  const kind = item?.item_kind;
  const label = String(item?.item_label || '').trim();
  if (kind === 'cadastral_lot') {
    const lotId = idOrEmpty(item.lot_id);
    return lotId ? { type: 'lot', lotId } : null;
  }
  if (kind === 'user_lot') {
    const userLotId = idOrEmpty(item.user_lot_id);
    return userLotId ? { type: 'unit', userLotId } : null;
  }
  if (kind === 'list') {
    const listId = idOrEmpty(item.list_id);
    return listId ? { type: 'list', listId, listName: label || 'Lista' } : null;
  }
  if (kind === 'showroom') {
    const projectId = idOrEmpty(item.project_id);
    return projectId ? { type: 'cad', projectId, parcelId: null, parcelLabel: null } : null;
  }
  if (kind === 'cad_parcel') {
    const projectId = idOrEmpty(item.project_id);
    const parcelId = idOrEmpty(item.parcel_id);
    return projectId && parcelId
      ? { type: 'cad', projectId, parcelId, parcelLabel: label || null }
      : null;
  }
  return null;
}

/**
 * Tooltip / aria del chip clicable.
 *
 * @param {object|null} target De `getLeadItemTarget`.
 * @returns {string}
 */
export function getLeadItemOpenLabel(target) {
  if (!target) return '';
  if (target.type === 'list') return 'Abrir lista';
  if (target.type === 'cad') return target.parcelId ? 'Abrir lote' : 'Abrir lista';
  if (target.type === 'unit') return 'Abrir la unidad';
  return 'Abrir lote';
}

/**
 * Origen “volver al lead” de MapPage: se arma al abrir un interés desde la
 * ficha (`useLeadItemOpener` manda `fromLead`). Mismo rol que
 * `openedFromRegistros`: el lote / la lista abiertos muestran la flecha atrás
 * y cerrarlos reabre Leads en esa ficha. Sin id → `null` (no hay volver).
 *
 * @param {{ leadId?: string, leadName?: string }|null|undefined} fromLead
 * @param {string|null} [ownerUserId] Owner del espacio activo (`null` = propio).
 * @returns {{ leadId: string, leadName: string, ownerUserId: string|null }|null}
 */
export function buildLeadReturnOrigin(fromLead, ownerUserId = null) {
  const leadId = String(fromLead?.leadId || '').trim();
  if (!leadId) return null;
  return {
    leadId,
    leadName: String(fromLead?.leadName || '').trim(),
    ownerUserId: ownerUserId || null,
  };
}

/**
 * Contexto del “volver” (subtítulo + “Volver a …”), como `Lista: {nombre}` /
 * `Registros`: **“Lead: Ana”**; sin nombre, “Lead”.
 *
 * @param {{ leadName?: string }|null} origin De `buildLeadReturnOrigin`.
 * @returns {string} `''` sin origen.
 */
export function getLeadReturnLabel(origin) {
  if (!origin) return '';
  const name = String(origin.leadName || '').trim();
  return name ? `Lead: ${name}` : 'Lead';
}

/**
 * Lead abierto desde "Consultas y leads" de un registro (UF o lote catastral):
 * el "volver" del detalle del lead reabre ese registro (inverso de
 * `buildLeadReturnOrigin`). Solo vale para ese lead; `ownerUserId` = espacio
 * activo (se chequea con `isLeadReturnOriginInSpace`). Sin `userLotId` vuelve
 * al lote.
 *
 * Con `parcelId` + `project.slug` vuelve a esa parcela CAD.
 *
 * @returns {{ leadId: string, lotId: string|null, userLotId: string|null, parcelId?: string,
 *   project?: object, label: string, ownerUserId: string|null }|null}
 */
export function buildLeadRecordReturn({
  leadId, lotId, userLotId, label, parcelId, project,
} = {}, ownerUserId = null) {
  const lead = String(leadId || '').trim();
  const lot = String(lotId || '').trim();
  const unit = String(userLotId || '').trim();
  const parcel = String(parcelId || '').trim();
  // Parcela CAD: vuelve con la misma apertura que el mapa (`project.slug` + parcela).
  if (lead && parcel && project?.slug) {
    return {
      leadId: lead,
      lotId: null,
      userLotId: null,
      parcelId: parcel,
      project: { ...project },
      label: String(label || '').trim() || 'la parcela',
      ownerUserId: ownerUserId || null,
    };
  }
  if (!lead || !lot) return null;
  return {
    leadId: lead,
    lotId: lot,
    userLotId: unit || null,
    label: String(label || '').trim() || (unit ? 'la unidad' : 'el lote'),
    ownerUserId: ownerUserId || null,
  };
}

/** Igual que `buildLeadRecordReturn`, pero exige la UF. */
export function buildLeadUnitReturn(origin = {}, ownerUserId = null) {
  if (!String(origin?.userLotId || '').trim()) return null;
  return buildLeadRecordReturn(origin, ownerUserId);
}

/**
 * ¿El origen es del espacio activo? Cambiar de espacio ya lo limpia; esto es
 * la red por si llega a quedar uno viejo (no se reabre un lead de otro owner).
 *
 * @param {{ ownerUserId?: string|null }|null} origin
 * @param {string|null} ownerUserId
 * @returns {boolean}
 */
export function isLeadReturnOriginInSpace(origin, ownerUserId = null) {
  if (!origin) return false;
  return (origin.ownerUserId || null) === (ownerUserId || null);
}

export const LEAD_ACTIVITY_LABELS = {
  created: 'Lead creado',
  updated: 'Datos actualizados',
  stage_changed: 'Cambio de etapa',
  note_added: 'Nota',
  item_added: 'Interés agregado',
  item_removed: 'Interés quitado',
  inquiry_linked: 'Consulta vinculada',
  // SQL 100: consulta cargada a mano (POST /createmanualinquiry).
  inquiry_manual_added: 'Consulta cargada',
  // M2: registro asignado después a una consulta manual abierta (PATCH /assigninquiryrecord).
  inquiry_record_assigned: 'Registro asignado',
  // SQL 098: archivar (DELETE /deletelead) y desarchivo automático por consulta nueva.
  archived: 'Lead archivado',
  unarchived: 'Desarchivado por una consulta nueva',
  // SQL 099: fusión de leads de la misma cuenta (un lead por cuenta).
  merged: 'Leads unificados',
  // V3-3: etapa de un interés.
  interest_stage_changed: 'Cambio de etapa del interés',
};

/**
 * Rol de quien hizo la acción (`actor.role`, § Trazabilidad). Dueño / Editor
 * igual que el historial de Espacios (`collectionActivityDisplay`).
 */
export const LEAD_ACTOR_ROLE_LABELS = {
  owner: 'Dueño',
  editor: 'Editor',
  ex_editor: 'Ex editor',
  system: 'Sistema',
};

/**
 * Quién hizo una acción del lead (`activity[].actor`, `created_by`,
 * `updated_by`). Como en Espacios: "email · Rol". `system` → solo "Sistema";
 * sin email (acciones viejas) → solo el rol. Sin `actor` (respuesta anterior a
 * la 086) o rol desconocido sin email → null: no se muestra nada.
 *
 * @param {{ user_id?: string|null, email?: string|null, role?: string }|null|undefined} actor
 * @returns {{ email: string, roleKey: string, roleLabel: string, text: string }|null}
 */
export function describeLeadActor(actor) {
  if (!actor || typeof actor !== 'object') return null;
  const roleKey = String(actor.role || '').trim();
  const roleLabel = LEAD_ACTOR_ROLE_LABELS[roleKey] || '';
  const email = roleKey === 'system' ? '' : String(actor.email || '').trim();
  if (!email && !roleLabel) return null;
  return {
    email,
    roleKey,
    roleLabel,
    text: [email, roleLabel].filter(Boolean).join(' · '),
  };
}

const LEAD_EDITED_MIN_GAP_MS = 60 * 1000;

/**
 * "Creado por / Editado por" de la ficha. "Editado por" solo si hubo un cambio
 * después del alta (`updated_at` más de 1 min después de `created_at`).
 *
 * @param {object} lead
 * @returns {{ key: 'created'|'updated', label: string, actor: object, at: string|null }[]}
 */
export function getLeadAuthorship(lead) {
  if (!lead || typeof lead !== 'object') return [];
  const rows = [];
  const created = describeLeadActor(lead.created_by);
  if (created) {
    rows.push({ key: 'created', label: 'Creado por', actor: created, at: lead.created_at || null });
  }
  const updated = describeLeadActor(lead.updated_by);
  const createdMs = new Date(lead.created_at).getTime();
  const updatedMs = new Date(lead.updated_at).getTime();
  const edited = Number.isFinite(updatedMs)
    && (!Number.isFinite(createdMs) || updatedMs - createdMs > LEAD_EDITED_MIN_GAP_MS);
  if (updated && edited) {
    rows.push({ key: 'updated', label: 'Editado por', actor: updated, at: lead.updated_at });
  }
  return rows;
}

export function getLeadStageLabel(stage) {
  return LEAD_STAGE_LABELS[stage] || stage || '—';
}

export function getLeadLostReasonLabel(reason) {
  return LEAD_LOST_REASON_LABELS[reason] || reason || '';
}

export function getLeadSourceLabel(source) {
  return LEAD_SOURCE_LABELS[source] || source || '';
}

function readLeadItemActivityParts(entry, items) {
  const payload = entry?.payload && typeof entry.payload === 'object' ? entry.payload : {};
  const itemId = idOrEmpty(payload.item_id);
  const current = itemId && Array.isArray(items) ? items.find((it) => idOrEmpty(it?.id) === itemId) : null;
  const kindLabel = LEAD_ITEM_KIND_LABELS[payload.item_kind || current?.item_kind] || '';
  const kind = payload.item_kind || current?.item_kind;
  const parts = getLeadItemLabelParts(payload.item_label || current?.item_label, kind);
  // Nomenclatura sola: el tipo ya va en el label (“Lote 1101…”), no “Lote: Lote 1101…”.
  return { kindLabel: parts.isCadastral ? '' : kindLabel, label: parts.text };
}

/**
 * Qué interés se sumó / quitó (`item_added` / `item_removed`), como el chip:
 * "Lote: Lote 42".
 *
 * - `item_removed` trae el snapshot (`item_label`, `item_kind`); las viejas
 *   solo `item_id` → '' (queda el título "Interés quitado").
 * - `item_added` trae `item_id` + `item_kind`: el label sale del ítem del lead
 *   si todavía existe (`options.items`).
 *
 * @param {object} entry Fila de `activity`.
 * @param {{ items?: object[] }} [options]
 * @returns {string}
 */
export function describeLeadItemActivity(entry, { items } = {}) {
  const { kindLabel, label } = readLeadItemActivityParts(entry, items);
  if (label && kindLabel) return `${kindLabel}: ${label}`;
  return label || kindLabel;
}

/**
 * Texto de una fila del timeline.
 *
 * @param {object} entry Fila de `activity`.
 * @param {{ items?: object[] }} [options] Ítems actuales del lead (label de `item_added`).
 * @returns {string}
 */
const LEAD_MERGE_COUNT_WORDS = [
  { match: /^inquir/, one: 'consulta', many: 'consultas' },
  { match: /^(item|interest)/, one: 'interés', many: 'intereses' },
  { match: /^note/, one: 'nota', many: 'notas' },
  { match: /^activit/, one: 'evento', many: 'eventos' },
];

/**
 * Resumen de `merged` (SQL 099): `source_lead_ids` + conteos. Los conteos se
 * leen de `payload.counts` o sueltos en el payload (`inquiries`,
 * `inquiries_moved`, `items_count`…); claves desconocidas o en 0 se omiten.
 * Ej.: "Se unificaron 2 leads de la misma cuenta · 3 consultas · 1 nota".
 *
 * @param {object} payload
 * @returns {string}
 */
export function describeLeadMergeActivity(payload) {
  const p = payload && typeof payload === 'object' ? payload : {};
  const sources = Array.isArray(p.source_lead_ids) ? p.source_lead_ids.filter(Boolean).length : 0;
  const head = sources
    ? `Se ${sources === 1 ? 'unificó 1 lead' : `unificaron ${sources} leads`} de la misma cuenta`
    : 'Se unificaron leads de la misma cuenta';
  const counts = p.counts && typeof p.counts === 'object' ? p.counts : p;
  const parts = [];
  LEAD_MERGE_COUNT_WORDS.forEach(({ match, one, many }) => {
    const key = Object.keys(counts).find((k) => match.test(k) && Number.isFinite(Number(counts[k])) && typeof counts[k] !== 'boolean' && !Array.isArray(counts[k]));
    const n = key ? Number(counts[key]) : 0;
    if (n > 0) parts.push(`${n} ${n === 1 ? one : many}`);
  });
  return [head, ...parts].join(' · ');
}

export function describeLeadActivity(entry, options = {}) {
  const action = entry?.action;
  const payload = entry?.payload && typeof entry.payload === 'object' ? entry.payload : {};
  if (action === 'stage_changed') {
    const from = getLeadStageLabel(payload.from);
    const to = getLeadStageLabel(payload.to);
    const reason = payload.lost_reason ? ` (${getLeadLostReasonLabel(payload.lost_reason)})` : '';
    // L2: "Reactivado por nueva consulta" viene en `note`.
    const note = String(entry?.note || '').trim();
    return `${from} → ${to}${reason}${note ? ` · ${note}` : ''}`;
  }
  if (action === 'interest_stage_changed') {
    const item = (Array.isArray(options?.items) ? options.items : []).find((i) => i?.id === payload.item_id);
    const parts = getLeadItemLabelParts(payload.item_label || item?.item_label, payload.item_kind || item?.item_kind);
    const from = payload.from ? getInterestStageLabel(payload.from) : '';
    const to = getInterestStageLabel(payload.to);
    const stages = from && payload.from !== payload.to ? `${from} → ${to}` : to;
    const offer = (payload.to === 'ofertado' || payload.to === 'reservado')
      ? formatInterestOffer(payload.offer_amount, payload.offer_currency) : '';
    const reason = payload.discard_reason ? getLeadLostReasonLabel(payload.discard_reason) : '';
    const note = String(entry?.note || '').trim();
    return [parts.text, stages, offer, reason, note].filter(Boolean).join(' · ');
  }
  if (action === 'note_added') return String(entry?.note || '').trim();
  if (action === 'merged') return describeLeadMergeActivity(payload);
  if (action === 'inquiry_record_assigned') {
    const parts = getLeadItemLabelParts(payload.item_label, payload.item_kind);
    const label = parts.text || LEAD_ITEM_KIND_LABELS[payload.item_kind] || '';
    return [
      label ? `A la consulta manual: ${label}` : 'A la consulta manual',
      payload.interest_reactivated ? 'reactivó el interés' : '',
    ].filter(Boolean).join(' · ');
  }
  if (action === 'inquiry_manual_added') {
    return String(entry?.note || '').trim() || describeManualInquiryMeta(payload, entry?.created_at);
  }
  if (action === 'inquiry_linked') {
    const note = String(entry?.note || '').trim();
    return note || describeInquiryLinkMeta(payload);
  }
  if (action === 'updated') {
    const fields = Array.isArray(payload.fields) ? payload.fields : [];
    const labels = {
      name: 'nombre', email: 'email', phone: 'teléfono', interest: 'interés', interest_filters: 'filtros',
      channel: 'canal',
    };
    // Perfil (contrato de datos del cliente): `profile.<campo>`, solo nombres.
    const leadFields = fields.filter((f) => !String(f).startsWith('profile'));
    const profileFields = fields.filter((f) => String(f).startsWith('profile'));
    const named = leadFields.map((f) => labels[f] || f).filter(Boolean);
    const profileNamed = [...new Set(profileFields
      .map((f) => LEAD_PROFILE_FIELD_LABELS[String(f).replace(/^profile\.?/, '')] || '')
      .filter(Boolean))];
    const parts = [
      named.length ? `Cambió ${named.join(', ')}` : '',
      profileFields.length
        ? `${named.length ? 'datos' : 'Editó datos'} del cliente${profileNamed.length ? `: ${profileNamed.join(', ')}` : ''}`
        : '',
    ].filter(Boolean);
    return parts.length ? parts.join(' · ') : LEAD_ACTIVITY_LABELS.updated;
  }
  if (action === 'item_added' || action === 'item_removed') {
    const { kindLabel, label: itemLabel } = readLeadItemActivityParts(entry, options?.items);
    const label = itemLabel || kindLabel;
    return label ? `${LEAD_ACTIVITY_LABELS[action]}: ${label}` : LEAD_ACTIVITY_LABELS[action];
  }
  return LEAD_ACTIVITY_LABELS[action] || action || '';
}

/**
 * Rótulo de grupo del timeline: Hoy, Ayer, Esta semana, Semana pasada o el mes.
 * Vive en `src/Utils/dayGroups.js` (compartido con Historial).
 *
 * @param {string|number|Date} value
 * @param {Date} [now]
 * @returns {string}
 */
export const getLeadActivityGroupLabel = getDayGroupLabel;

/**
 * Actividad más nueva arriba, partida en grupos de día.
 *
 * @param {object[]} entries
 * @param {Date} [now]
 * @returns {{ label: string, entries: object[] }[]}
 */
export function groupLeadActivity(entries, now = new Date()) {
  return groupByDay(entries, (entry) => entry?.created_at, now);
}

const LEAD_ACTIVITY_ITEM_ACTIONS = new Set(['item_added', 'item_removed', 'interest_stage_changed', 'inquiry_record_assigned']);

const LEAD_ACTIVITY_RECORD_KEYS = [
  ['cad_parcel', 'parcel_id'],
  ['user_lot', 'user_lot_id'],
  ['cadastral_lot', 'lot_id'],
  ['list', 'list_id'],
  ['showroom', 'project_id'],
];

function leadActivityPayload(entry) {
  return entry?.payload && typeof entry.payload === 'object' ? entry.payload : {};
}

function leadActivityRecordMatches(payload, item) {
  const kind = payload?.item_kind || item?.item_kind;
  const ref = LEAD_ACTIVITY_RECORD_KEYS.find(([key]) => key === kind);
  if (!ref) return false;
  if (payload?.item_kind && item?.item_kind && payload.item_kind !== item.item_kind) return false;
  const id = idOrEmpty(payload?.[ref[1]]);
  if (!id || id !== idOrEmpty(item?.[ref[1]])) return false;
  if (kind === 'cad_parcel') {
    const projectId = idOrEmpty(payload?.project_id);
    const itemProjectId = idOrEmpty(item?.project_id);
    if (projectId && itemProjectId && projectId !== itemProjectId) return false;
  }
  return true;
}

/**
 * A qué interés pertenece una fila del timeline.
 * Id del interés, `'others'` (consulta sin interés) o `null` (evento del lead:
 * nota, alta, datos). Esos últimos solo entran en Todos.
 *
 * @param {object} entry
 * @param {{ item: object, inquiries?: object[] }[]} groups `groupLeadInterests().groups`
 * @param {object[]} [others]
 * @returns {string|null}
 */
export function leadActivityInterestKey(entry, groups, others = []) {
  const payload = leadActivityPayload(entry);
  const list = Array.isArray(groups) ? groups : [];
  const loose = Array.isArray(others) ? others : [];
  const action = entry?.action;

  const byItemId = (id) => {
    const key = idOrEmpty(id);
    if (!key) return '';
    return list.some((group) => idOrEmpty(group?.item?.id) === key) ? key : '';
  };

  const byRecord = () => {
    const matches = list.filter((group) => leadActivityRecordMatches(payload, group?.item));
    return matches.length === 1 ? idOrEmpty(matches[0].item?.id) : '';
  };

  if (LEAD_ACTIVITY_ITEM_ACTIONS.has(action)) {
    return byItemId(payload.item_id) || byRecord() || null;
  }

  if (isLeadInquiryActivity(action)) {
    const inquiryId = idOrEmpty(payload.inquiry_id);
    if (inquiryId) {
      const owner = list.find((group) => (group?.inquiries || []).some((inq) => idOrEmpty(inq?.id) === inquiryId));
      if (owner) return idOrEmpty(owner.item?.id);
      if (loose.some((inq) => idOrEmpty(inq?.id) === inquiryId)) return 'others';
    }
    const itemId = byItemId(payload.item_id);
    if (itemId) return itemId;
    const recordId = byRecord();
    if (recordId) return recordId;
    const label = String(payload.item_label || '').trim();
    if (label) {
      const named = list.filter((group) => String(group?.item?.item_label || '').trim() === label);
      if (named.length === 1) return idOrEmpty(named[0].item?.id);
    }
    return 'others';
  }

  return null;
}

/**
 * Chips del filtro del detalle: un interés por chip, en el orden de las tarjetas.
 * "Otras consultas" si hay consultas sueltas o un evento que no cae en ningún interés.
 *
 * @param {{ item: object, inquiries?: object[] }[]} groups
 * @param {object[]} entries
 * @param {object[]} [others]
 * @returns {{ id: string, label: string }[]}
 */
export function leadActivityFilterOptions(groups, entries, others = []) {
  const list = Array.isArray(groups) ? groups : [];
  const options = list
    .map((group) => {
      const item = group?.item;
      const parts = getLeadItemLabelParts(item?.item_label, item?.item_kind);
      return { id: idOrEmpty(item?.id), label: parts.text || 'Interés' };
    })
    .filter((option) => option.id);
  const loose = Array.isArray(others) ? others : [];
  const hasOthers = loose.length > 0 || (Array.isArray(entries) ? entries : []).some(
    (entry) => leadActivityInterestKey(entry, list, loose) === 'others',
  );
  if (hasOthers) options.push({ id: 'others', label: 'Otras consultas' });
  return options;
}

/**
 * @param {object[]} entries
 * @param {string} filterId `'all'` o un id de `leadActivityFilterOptions`
 * @param {{ item: object, inquiries?: object[] }[]} groups
 * @param {object[]} [others]
 * @returns {object[]}
 */
export function filterLeadActivityEntries(entries, filterId, groups, others = []) {
  const list = Array.isArray(entries) ? entries : [];
  if (!filterId || filterId === 'all') return list;
  return list.filter((entry) => leadActivityInterestKey(entry, groups, others) === filterId);
}

/**
 * Línea secundaria de una actividad `inquiry_linked`: cómo se vinculó y el ítem.
 *
 * @param {object} payload
 * @returns {string}
 */
export function describeInquiryLinkMeta(payload) {
  const p = payload && typeof payload === 'object' ? payload : {};
  const how = p.link === 'manual'
    ? 'Vinculada a mano'
    : p.link === 'backfill'
      ? 'Consulta anterior'
      : p.collection_kind === 'lot_share'
        ? 'Desde un lote compartido'
        : 'Desde un enlace compartido';
  const parts = [how];
  if (p.lead_created) parts.push('creó el lead');
  if (p.reopened) parts.push('reactivó el lead');
  const item = getLeadItemLabelParts(p.item_label, p.item_kind).text || LEAD_ITEM_KIND_LABELS[p.item_kind] || '';
  if (item) parts.push(item);
  return parts.join(' · ');
}

/**
 * Línea secundaria de `inquiry_manual_added`: registro (o “Sin registro”),
 * “creó el lead” y la fecha de la consulta si no es la de la carga.
 *
 * @param {object} payload
 * @param {string} [loggedAt] `created_at` de la actividad (momento de la carga).
 * @returns {string}
 */
export function describeManualInquiryMeta(payload, loggedAt = '') {
  const p = payload && typeof payload === 'object' ? payload : {};
  const parts = [];
  const item = getLeadItemLabelParts(p.item_label, p.item_kind).text || LEAD_ITEM_KIND_LABELS[p.item_kind] || '';
  parts.push(item || 'Sin registro');
  if (p.lead_created) parts.push('creó el lead');
  const occurred = Date.parse(p.occurred_at || '');
  const logged = Date.parse(loggedAt || '');
  // Más de 1 min entre la consulta y la carga → se dice cuándo fue.
  if (Number.isFinite(occurred) && (!Number.isFinite(logged) || Math.abs(logged - occurred) > 60 * 1000)) {
    const dt = new Date(occurred);
    const day = dt.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit' });
    const hm = dt.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit', hour12: false });
    parts.push(`Consulta del ${day} ${hm}`);
  }
  return parts.join(' · ');
}

/**
 * Junta las `inquiry_manual_added` de una misma carga (`batch_id`, 2+
 * registros) en una sola entrada del timeline. La entrada resultante es la
 * primera del lote (por `batch_index`) con `batch: [entries]`. El resto pasa
 * igual. Se aplica después del filtro por registro (con un registro elegido
 * queda solo su consulta).
 *
 * @param {object[]} entries
 * @returns {object[]}
 */
export function groupManualInquiryBatches(entries) {
  const list = Array.isArray(entries) ? entries : [];
  const batches = new Map();
  list.forEach((entry) => {
    const batchId = entry?.action === 'inquiry_manual_added' ? idOrEmpty(entry?.payload?.batch_id) : '';
    if (!batchId) return;
    if (!batches.has(batchId)) batches.set(batchId, []);
    batches.get(batchId).push(entry);
  });
  const emitted = new Set();
  const out = [];
  list.forEach((entry) => {
    const batchId = entry?.action === 'inquiry_manual_added' ? idOrEmpty(entry?.payload?.batch_id) : '';
    const members = batchId ? batches.get(batchId) : null;
    if (!members || members.length < 2) {
      out.push(entry);
      return;
    }
    if (emitted.has(batchId)) return;
    emitted.add(batchId);
    const sorted = members.slice().sort((a, b) => (Number(a?.payload?.batch_index) || 0) - (Number(b?.payload?.batch_index) || 0));
    out.push({ ...sorted[0], id: `batch:${batchId}`, batch: sorted });
  });
  return out;
}

/**
 * Línea secundaria de una carga manual con varios registros: “3 registros:
 * UF 1, UF 2, Lote 9” (+ “creó el lead” / fecha real como la simple).
 *
 * @param {object[]} batch Entradas del lote (ordenadas).
 * @returns {string}
 */
export function describeManualInquiryBatchMeta(batch) {
  const list = Array.isArray(batch) ? batch : [];
  if (!list.length) return '';
  const labels = list.map((entry) => {
    const p = entry?.payload || {};
    return getLeadItemLabelParts(p.item_label, p.item_kind).text || LEAD_ITEM_KIND_LABELS[p.item_kind] || '';
  }).filter(Boolean);
  const head = `${list.length} registros${labels.length ? `: ${labels.join(', ')}` : ''}`;
  const first = list[0];
  const rest = describeManualInquiryMeta(first?.payload, first?.created_at).split(' · ').slice(1);
  return [head, ...rest].join(' · ');
}

/**
 * `contact_diff` de `inquiry_linked`: qué datos trajo la consulta distintos
 * del lead (nunca se pisan). `[{ field, lead, inquiry }]`.
 *
 * @param {object} payload
 * @returns {{ field: string, label: string, lead: string, inquiry: string }[]}
 */
export function readInquiryContactDiff(payload) {
  const diff = payload?.contact_diff;
  if (!diff || typeof diff !== 'object') return [];
  const labels = { name: 'Nombre', email: 'Email', phone: 'Teléfono' };
  return Object.entries(diff)
    .map(([field, value]) => ({
      field,
      label: labels[field] || field,
      lead: String(value?.lead ?? '').trim(),
      inquiry: String(value?.inquiry ?? '').trim(),
    }))
    .filter((row) => row.inquiry);
}

/**
 * Contadores por etapa. Tolera `stats` ausente o parcial.
 *
 * @param {object|null|undefined} stats `stats` de GET /getleads.
 * @returns {{ total: number, byStage: Record<string, number> }}
 */
export function normalizeLeadStats(stats) {
  const byStageRaw = stats?.by_stage && typeof stats.by_stage === 'object' ? stats.by_stage : {};
  const byStage = {};
  LEAD_STAGES.forEach((stage) => {
    const n = Number(byStageRaw[stage]);
    byStage[stage] = Number.isFinite(n) && n > 0 ? Math.trunc(n) : 0;
  });
  const byTempRaw = stats?.by_temperature && typeof stats.by_temperature === 'object'
    ? stats.by_temperature
    : {};
  const byTemperature = {};
  LEAD_TEMPERATURES.forEach((temp) => {
    const n = Number(byTempRaw[temp]);
    byTemperature[temp] = Number.isFinite(n) && n > 0 ? Math.trunc(n) : 0;
  });
  const totalRaw = Number(stats?.total);
  const total = Number.isFinite(totalRaw) && totalRaw >= 0
    ? Math.trunc(totalRaw)
    : Math.max(
      LEAD_STAGES.reduce((acc, stage) => acc + byStage[stage], 0),
      LEAD_TEMPERATURES.reduce((acc, temp) => acc + byTemperature[temp], 0),
    );
  const unseenRaw = Number(stats?.unseen);
  const unseen = Number.isFinite(unseenRaw) && unseenRaw > 0 ? Math.trunc(unseenRaw) : 0;
  return { total, byStage, byTemperature, unseen };
}

/**
 * Conteos de los chips de etapa, coherentes con la lista que se ve.
 *
 * `stats` (y `list_stats`) de getleads son de **todo** el owner / la lista:
 * no siguen a `source`, `via` ni `q`, y no se enteran de lo que se editó en
 * la ficha hasta el próximo GET. Por eso:
 * - Página completa (`hasMore` false): se cuenta lo cargado. Ya viene filtrado
 *   por origen / búsqueda / lista y tiene las ediciones locales → exacto.
 * - Más de una página sin origen ni búsqueda: los del servidor (exactos;
 *   `list_stats` si hay filtro de lista).
 * - Más de una página con origen o búsqueda: la API no da conteos filtrados →
 *   lo cargado (piso; el resumen ya muestra “200+”). `lowerBound: true`.
 *
 * @param {{ leads: object[], hasMore: boolean, serverStats: object|null, narrowed: boolean }} input
 * @returns {{ total: number, byStage: Record<string, number>, lowerBound: boolean }}
 */
export function resolveLeadStageCounts({ leads = [], hasMore = false, serverStats = null, narrowed = false }) {
  if (hasMore && !narrowed && serverStats) {
    return { ...normalizeLeadStats(serverStats), lowerBound: false };
  }
  const byStage = {};
  LEAD_STAGES.forEach((stage) => { byStage[stage] = 0; });
  (Array.isArray(leads) ? leads : []).forEach((lead) => {
    if (lead && byStage[lead.stage] !== undefined) byStage[lead.stage] += 1;
  });
  const list = Array.isArray(leads) ? leads : [];
  return { total: list.length, byStage, lowerBound: Boolean(hasMore) };
}

/**
 * Conteos de los chips de temperatura (tablero V3).
 *
 * `stats.by_temperature` es global del owner (como `by_stage`). Si viene en
 * la respuesta, se usa siempre para los chips: con filtro `temperature=` la
 * página solo trae esa temperatura y no se puede reconstruir el resto.
 * Sin `by_temperature`, se cuenta lo cargado (piso si `hasMore`).
 *
 * @param {{ leads: object[], hasMore?: boolean, serverStats: object|null }} input
 * @returns {{ total: number, byTemperature: Record<string, number>, byStage: Record<string, number>, lowerBound: boolean }}
 */
export function resolveLeadTemperatureCounts({ leads = [], hasMore = false, serverStats = null }) {
  if (serverStats?.by_temperature && typeof serverStats.by_temperature === 'object') {
    return { ...normalizeLeadStats(serverStats), lowerBound: false };
  }
  if (hasMore && serverStats) {
    return { ...normalizeLeadStats(serverStats), lowerBound: false };
  }
  const byTemperature = {};
  LEAD_TEMPERATURES.forEach((temp) => { byTemperature[temp] = 0; });
  (Array.isArray(leads) ? leads : []).forEach((lead) => {
    if (lead && byTemperature[lead.temperature] !== undefined) byTemperature[lead.temperature] += 1;
  });
  const list = Array.isArray(leads) ? leads : [];
  const base = normalizeLeadStats(serverStats);
  return {
    total: serverStats ? base.total : list.length,
    byStage: base.byStage,
    byTemperature,
    lowerBound: Boolean(hasMore),
  };
}

function toCount(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? Math.trunc(n) : 0;
}

const pluralLeads = (n) => (n === 1 ? 'lead' : 'leads');

/**
 * Resumen chico del índice de Leads (a la izquierda de “Ordenar por”).
 *
 * - Sin filtros: “6 leads · 2 consultas sin ver” (sin la segunda parte si es 0).
 * - Con filtros: “2 de 6 leads · Nuevo · Consultas · “ana”” (las etiquetas
 *   las arma quien llama, en el orden etapa → origen → búsqueda).
 *
 * `total` es el de `stats` (todo el owner). `visibleIsLowerBound` → “200+”:
 * la API cortó en el límite y lo visible no es el número real.
 *
 * @param {{ total: number, visible?: number, visibleIsLowerBound?: boolean,
 *   unseenInquiries?: number, filterLabels?: string[] }} input
 * @returns {{ filtered: boolean, text: string }}
 */
export function describeLeadsSummary({
  total,
  visible = 0,
  visibleIsLowerBound = false,
  unseenInquiries = 0,
  filterLabels = [],
}) {
  const totalCount = toCount(total);
  const labels = (Array.isArray(filterLabels) ? filterLabels : []).filter(Boolean);
  if (!labels.length) {
    const parts = [`${totalCount} ${pluralLeads(totalCount)}`];
    const unseen = toCount(unseenInquiries);
    if (unseen > 0) parts.push(unseen === 1 ? '1 consulta sin ver' : `${unseen} consultas sin ver`);
    return { filtered: false, text: parts.join(' · ') };
  }
  const shown = `${toCount(visible)}${visibleIsLowerBound ? '+' : ''}`;
  return {
    filtered: true,
    text: [`${shown} de ${totalCount} ${pluralLeads(totalCount)}`, ...labels].join(' · '),
  };
}

/** Misma regla que auth / consultas. Vacío = ok (el contacto puede ser solo teléfono). */
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const PHONE_CHARS_REGEX = /^[\d\s+\-().]*$/;
const PHONE_MIN_DIGITS = 8;
const PHONE_MAX_DIGITS = 15;

/**
 * Email del lead. Vacío es válido (contacto opcional si hay teléfono).
 *
 * @param {string} value
 * @returns {boolean}
 */
export function isPlausibleEmail(value) {
  const v = String(value || '').trim();
  return !v || EMAIL_REGEX.test(v);
}

/**
 * Teléfono del lead. Vacío = ok; si hay valor, chars de teléfono y 8–15 dígitos
 * (misma regla que consultas / marcas).
 *
 * @param {string} value
 * @returns {boolean}
 */
export function isPlausiblePhone(value) {
  const trimmed = String(value || '').trim();
  if (!trimmed) return true;
  if (!PHONE_CHARS_REGEX.test(trimmed)) return false;
  const digits = trimmed.replace(/\D/g, '');
  return digits.length >= PHONE_MIN_DIGITS && digits.length <= PHONE_MAX_DIGITS;
}

function inquiryTime(inq) {
  const t = Date.parse(getInquiryDisplayAt(inq));
  return Number.isFinite(t) ? t : 0;
}

function interestTime(item, inquiries) {
  const times = inquiries.map(inquiryTime);
  ['last_inquiry_at', 'stage_changed_at', 'updated_at', 'created_at'].forEach((key) => {
    const t = Date.parse(item?.[key] || '');
    if (Number.isFinite(t)) times.push(t);
  });
  return times.length ? Math.max(...times) : 0;
}

/**
 * Bloque "Intereses y consultas" del detalle (V3-3): un grupo por interés con
 * sus consultas adentro (`items[].inquiries[]` de getlead, más nuevas primero)
 * y las consultas raíz que no son de ningún interés (lista / showroom sin
 * registro) en `others`. Match por id contra las anidadas; si la API no anida
 * (`item.inquiries` ausente), cae al registro (`findInquiryInterest`).
 * Intereses ordenados por la última consulta o actividad, más reciente primero.
 *
 * @param {object[]} items
 * @param {object[]|null} rootInquiries
 * @returns {{ groups: { item: object, inquiries: object[], total: number }[], others: object[] }}
 */
export function groupLeadInterests(items, rootInquiries) {
  const list = Array.isArray(items) ? items.filter(Boolean) : [];
  const root = Array.isArray(rootInquiries) ? rootInquiries.filter(Boolean) : [];
  const nestedIds = new Set();
  list.forEach((item) => {
    (Array.isArray(item.inquiries) ? item.inquiries : []).forEach((inq) => {
      if (inq?.id) nestedIds.add(String(inq.id));
    });
  });
  const fallback = new Map();
  const others = [];
  root.forEach((inq) => {
    if (inq.id && nestedIds.has(String(inq.id))) return;
    const owner = findInquiryInterest(inq, list.filter((it) => !Array.isArray(it.inquiries)));
    if (owner) {
      if (!fallback.has(owner.id)) fallback.set(owner.id, []);
      fallback.get(owner.id).push(inq);
    } else {
      others.push(inq);
    }
  });
  const byNewest = (a, b) => inquiryTime(b) - inquiryTime(a);
  const groups = list.map((item, index) => {
    const own = Array.isArray(item.inquiries) ? item.inquiries.filter(Boolean) : (fallback.get(item.id) || []);
    const inquiries = [...own].sort(byNewest);
    const count = Number(item.inquiry_count);
    const total = Number.isFinite(count) && count > inquiries.length ? count : inquiries.length;
    return { item, inquiries, total, index, at: interestTime(item, inquiries) };
  });
  groups.sort((a, b) => (b.at - a.at) || (a.index - b.index));
  return {
    groups: groups.map(({ item, inquiries, total }) => ({ item, inquiries, total })),
    others: others.sort(byNewest),
  };
}

/**
 * Resumen comercial por registro (Pipeline A, `commercial_summary` /
 * `commercial_rollup`). Reusa la paleta de etapas de interés y suma
 * `con_consultas`. `sin_interes` no se pinta (sin indicador).
 * @see docs/frontend/commercial-summary.md
 */
export const COMMERCIAL_STATUS_META = {
  sin_interes: { label: 'Sin interés', color: null },
  con_consultas: { label: 'Con consultas', color: '#9AA5B1' },
  ...Object.fromEntries(INTEREST_STAGES.map((s) => [s, {
    label: INTEREST_STAGE_LABELS[s],
    color: INTEREST_STAGE_COLORS[s],
  }])),
};
